import { getBreakpoint } from '@sg-core/resources/assets/js/utils/breakpoints';

/**
 * Controls the sticky add-to-bag behavior and the desktop sticky details offset
 * for one product banner instance.
 */
type StickyBarState = 'before' | 'during' | 'after';
type StickyBarVariant = 'before' | 'after';

export class ProductBanner {
  private static readonly selectors = {
    cta: '[data-product-banner-cta]',
    details: '.phxsg-product-banner__details',
    navigation: '.phxsg-navigation',
  };

  private static readonly desktopDetailsOffsetProperty = '--comp-product-banner-sticky-offset';
  private static readonly stickyBarHeightProperty = '--comp-product-banner-sticky-height';

  private readonly root: HTMLElement;
  private readonly controller = new AbortController();
  private readonly desktopMedia = window.matchMedia(`(min-width: ${getBreakpoint('md')}px)`);
  private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  private desktopDetails: HTMLElement | null = null;
  private desktopDetailsArePinned = false;
  private desktopDetailsOffsetIdleFrames = 0;
  private desktopDetailsOffsetLastValue = -1;
  private desktopDetailsOffsetRafId = 0;
  private inPageCta: HTMLElement | null = null;
  private navigation: HTMLElement | null = null;
  private stickyBar: HTMLElement | null = null;
  private stickyBarFullyHidden = false;
  private stickyBarObserver: IntersectionObserver | null = null;
  private stickyBarShownVariant: StickyBarVariant = 'before';
  private stickyBarTarget: StickyBarState = 'before';

  constructor(elementId: string) {
    const root = document.getElementById(elementId);

    if (!root) {
      throw new Error('ProductBanner element not found');
    }

    this.root = root;
    this.initStickyBar();
    this.initDesktopDetailsOffset();
  }

  private initStickyBar(): void {
    this.inPageCta = this.root.querySelector<HTMLElement>(ProductBanner.selectors.cta);
    // The bar lives in the page flow before the footer (not inside the banner section),
    // so it docks above the footer via native position:sticky. Find it by the banner id.
    this.stickyBar = document.querySelector<HTMLElement>(`[data-product-banner-sticky="${this.root.id}"]`);

    if (!this.inPageCta || !this.stickyBar) {
      return;
    }

    this.stickyBarShownVariant = this.stickyBar.dataset.stickyVariant === 'after' ? 'after' : 'before';
    this.stickyBar.addEventListener('transitionend', this.onStickyBarTransitionEnd, {
      signal: this.controller.signal,
    });
    this.desktopMedia.addEventListener('change', this.renderStickyBar, {
      signal: this.controller.signal,
    });
    this.createStickyBarObserver();

    window.addEventListener('resize', this.createStickyBarObserver, {
      passive: true,
      signal: this.controller.signal,
    });
    window.addEventListener('scroll', this.syncStickyBarDocked, {
      passive: true,
      signal: this.controller.signal,
    });
  }

  private createStickyBarObserver = (): void => {
    if (!this.inPageCta || !this.stickyBar) {
      return;
    }

    this.stickyBarObserver?.disconnect();

    /**
     * The bar covers the bottom of the viewport over its own height, so shrinking the
     * root by that height keeps the CTA counted as hidden until it clears the bar.
     */
    this.stickyBarObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (!entry) {
          return;
        }

        if (entry.isIntersecting) {
          this.onStickyBarState('during');
        } else if (entry.boundingClientRect.top < 0) {
          this.onStickyBarState('after');
        } else {
          this.onStickyBarState('before');
        }
      },
      {
        rootMargin: `0px 0px -${this.stickyBar.offsetHeight}px 0px`,
        threshold: 0,
      },
    );

    this.stickyBarObserver.observe(this.inPageCta);
    this.syncStickyBarHeight();
    this.syncStickyBarDocked();
  };

  private syncStickyBarDocked = (): void => {
    if (!this.stickyBar) {
      return;
    }

    const docked = this.stickyBar.getBoundingClientRect().bottom >= window.innerHeight - 1;
    const value = docked ? 'true' : 'false';

    if (this.stickyBar.dataset.stickyDocked !== value) {
      this.stickyBar.dataset.stickyDocked = value;
    }
  };

  private syncStickyBarHeight(): void {
    if (!this.stickyBar) {
      return;
    }

    document.documentElement.style.setProperty(
      ProductBanner.stickyBarHeightProperty,
      `${this.stickyBar.offsetHeight}px`,
    );
  }

  private onStickyBarTransitionEnd = (event: TransitionEvent): void => {
    if (!this.stickyBar) {
      return;
    }

    const leaveProperty = this.reducedMotion.matches ? 'opacity' : 'transform';

    if (
      event.target === this.stickyBar &&
      event.propertyName === leaveProperty &&
      this.stickyBar.dataset.stickyMobileVisible === 'false'
    ) {
      this.stickyBarFullyHidden = true;
      this.renderStickyBarMobileState();
    }
  };

  private onStickyBarState(state: StickyBarState): void {
    this.stickyBarTarget = state;
    this.renderStickyBar();
  }

  private renderStickyBar = (): void => {
    this.renderStickyBarMobileState();
    this.renderStickyBarDesktopState();
  };

  private renderStickyBarMobileState(): void {
    if (!this.stickyBar) {
      return;
    }

    if (this.desktopMedia.matches) {
      this.setStickyBarMobileVisible(false);
      this.stickyBarFullyHidden = true;

      return;
    }

    if (this.stickyBarTarget === 'during') {
      this.setStickyBarMobileVisible(false);

      return;
    }

    if (this.stickyBarTarget === this.stickyBarShownVariant) {
      this.setStickyBarMobileVisible(true);

      return;
    }

    if (this.stickyBarFullyHidden) {
      this.stickyBarShownVariant = this.stickyBarTarget;
      this.stickyBar.dataset.stickyVariant = this.stickyBarTarget;
      this.setStickyBarMobileVisible(true);
      this.syncStickyBarHeight();
    } else {
      this.setStickyBarMobileVisible(false);
    }
  }

  private setStickyBarMobileVisible(visible: boolean): void {
    if (!this.stickyBar) {
      return;
    }

    const value = visible ? 'true' : 'false';

    if (this.stickyBar.dataset.stickyMobileVisible !== value) {
      this.stickyBar.dataset.stickyMobileVisible = value;
    }

    if (visible) {
      this.stickyBarFullyHidden = false;
    }
  }

  private renderStickyBarDesktopState(): void {
    this.setStickyBarDesktopVisible(this.desktopMedia.matches && this.stickyBarTarget === 'after');
  }

  private setStickyBarDesktopVisible(visible: boolean): void {
    if (!this.stickyBar) {
      return;
    }

    const value = visible ? 'true' : 'false';

    if (this.stickyBar.dataset.stickyDesktopVisible !== value) {
      this.stickyBar.dataset.stickyDesktopVisible = value;
    }
  }

  private initDesktopDetailsOffset(): void {
    this.navigation = document.querySelector<HTMLElement>(ProductBanner.selectors.navigation);
    this.desktopDetails = this.root.querySelector<HTMLElement>(ProductBanner.selectors.details);

    if (!this.navigation || !this.desktopDetails) {
      return;
    }

    this.desktopDetailsArePinned = this.isDesktopDetailsPinned();

    window.addEventListener('scroll', this.startDesktopDetailsOffsetSync, {
      passive: true,
      signal: this.controller.signal,
    });
    window.addEventListener('resize', this.onDesktopDetailsOffsetResize, {
      passive: true,
      signal: this.controller.signal,
    });

    this.startDesktopDetailsOffsetSync();
  }

  private onDesktopDetailsOffsetResize = (): void => {
    this.desktopDetailsArePinned = this.isDesktopDetailsPinned();
    this.startDesktopDetailsOffsetSync();
  };

  private startDesktopDetailsOffsetSync = (): void => {
    this.desktopDetailsOffsetIdleFrames = 0;

    if (!this.desktopDetailsOffsetRafId) {
      this.desktopDetailsOffsetRafId = requestAnimationFrame(this.tickDesktopDetailsOffset);
    }
  };

  private tickDesktopDetailsOffset = (): void => {
    this.applyDesktopDetailsOffset();
    this.desktopDetailsOffsetRafId =
      this.desktopDetailsOffsetIdleFrames < 5 ? requestAnimationFrame(this.tickDesktopDetailsOffset) : 0;
  };

  private applyDesktopDetailsOffset(): void {
    if (!this.navigation) {
      return;
    }

    const offset = this.desktopDetailsArePinned
      ? Math.max(0, Math.round(this.navigation.getBoundingClientRect().bottom))
      : 0;

    if (offset === this.desktopDetailsOffsetLastValue) {
      this.desktopDetailsOffsetIdleFrames += 1;

      return;
    }

    this.root.style.setProperty(ProductBanner.desktopDetailsOffsetProperty, `${offset}px`);
    this.desktopDetailsOffsetLastValue = offset;
    this.desktopDetailsOffsetIdleFrames = 0;
  }

  private isDesktopDetailsPinned(): boolean {
    return this.desktopDetails ? getComputedStyle(this.desktopDetails).position === 'sticky' : false;
  }
}
